// Number / address formatting utilities - pure functions, no React.
// Used by every page in PR-1.4. Keep deterministic and locale-free so
// downstream tests can compare strings without ICU drift.

import { figureKey, readLastKnownFigure, writeLastKnownFigure } from './lastKnownFigure'
import { canonicalTonAddress } from './tonAddress'

const DASH = '—'

/**
 * A number as a plain decimal string — the only form the formatters below
 * accept.
 *
 * `String(n)` switches to scientific notation under 1e-6 and at/above 1e21, and
 * every formatter here parses with a plain-decimal regex, so an exponential
 * string falls through to the unavailable marker. That turned a KNOWN value
 * into "—" on the operator's wallet: after a percentage series drained the
 * position the chain getter reported 3 raw units, Holding was
 * 6.918949168078361e-11 GRAM, and the panel showed an em-dash — the marker that
 * means "we cannot say" standing in front of a number we could say exactly.
 *
 * The expansion is textual, so nothing is rounded on the way through.
 */
export function plainDecimal(value: number): string {
  const text = String(value)
  const match = /^([+-]?)(\d+)(?:\.(\d+))?[eE]([+-]?\d+)$/.exec(text)
  if (!match) return text
  const [, rawSign = '', whole = '', fraction = '', exponent = '0'] = match
  const sign = rawSign === '-' ? '-' : ''
  const digits = `${whole}${fraction}`
  // Where the decimal point lands inside `digits` once the exponent is applied.
  const point = whole.length + Number(exponent)
  if (point <= 0) return `${sign}0.${'0'.repeat(-point)}${digits}`
  if (point >= digits.length) return `${sign}${digits}${'0'.repeat(point - digits.length)}`
  return `${sign}${digits.slice(0, point)}.${digits.slice(point)}`
}

// Smart price formatter. Keep user-facing prices decimal: raw scientific
// notation makes small TON prices look broken even when the value is correct.
export function formatPrice(v: number): string {
  if (!Number.isFinite(v) || v === 0) return DASH
  const abs = Math.abs(v)
  if (abs < 0.0000000001) return v < 0 ? '<-0.0000000001' : '<0.0000000001'
  if (abs < 0.0001) return trimFixed(v, 10)
  if (abs < 1) return trimFixed(v, 8)
  if (abs < 1000) return v.toFixed(4)
  return v.toFixed(2)
}

// Compact volume / MC / liquidity: 1.2K, 1.2M, 1.2B. Two decimals down to 0.01;
// below that a value keeps TWO SIGNIFICANT DIGITS via formatSmallMoney, so a
// dust volume renders "$0.00016" instead of the 9-character "$0.0001683" that
// blew out the column, and 1e-9 renders "0.0₈1" instead of "<0.00000001".
// Positive tiny amounts still never collapse to a misleading "0.00".
export function formatCompact(v: number): string {
  if (!Number.isFinite(v)) return DASH
  const abs = Math.abs(v)
  if (abs > 0 && abs < 0.01) return formatSmallMoney(v)
  return `${compactScaled(v, abs, 2)}`
}

// Compact units, ascending. Plain (no suffix) is a tier so the roll-up rule
// below covers 999.996 -> "1.00K" with the same code as 999_999_999.5 -> "1.00B".
// Past T there is no universal convention, so these follow the ones already in
// use around crypto supplies: Q quadrillion, Qi quintillion, Sx sextillion.
// They are not decoration — a 1e15 supply is ordinary for a meme jetton, and
// without a tier it renders as the unreadable "1000.00T".
const COMPACT_UNITS = [
  { scale: 1, suffix: '' },
  { scale: 1e3, suffix: 'K' },
  { scale: 1e6, suffix: 'M' },
  { scale: 1e9, suffix: 'B' },
  { scale: 1e12, suffix: 'T' },
  { scale: 1e15, suffix: 'Q' },
  { scale: 1e18, suffix: 'Qi' },
  { scale: 1e21, suffix: 'Sx' },
] as const

// The unit must be chosen from the ROUNDED figure, not the raw one. Choosing it
// first lets two decimals round a value up out of its own tier and print the
// impossible "1000.00M" — which is 1B written the long way. That is what the
// pair page's Supply cell showed: Supply is derived (mcap_usd / price_usd), so
// on a 1B-supply token the ratio lands just under 1e9 as often as on it, and the
// label flickered 1.00B <-> 1000.00M on every trade. Roll up instead.
function compactScaled(v: number, abs: number, places: number): string {
  let tier = 0
  while (
    tier + 1 < COMPACT_UNITS.length &&
    abs >= (COMPACT_UNITS[tier + 1]?.scale ?? Number.POSITIVE_INFINITY)
  ) {
    tier += 1
  }
  let unit = COMPACT_UNITS[tier] ?? COMPACT_UNITS[0]
  let text = (v / unit.scale).toFixed(places)
  if (Math.abs(Number(text)) >= 1000 && tier + 1 < COMPACT_UNITS.length) {
    tier += 1
    unit = COMPACT_UNITS[tier] ?? unit
    text = (v / unit.scale).toFixed(places)
  }
  return `${text}${unit.suffix}`
}

// Exact-string counterpart of formatCompact for raw/decimals-derived activity
// amounts. It intentionally never converts the value through JavaScript Number:
// the visible compact form follows the trade rows while the caller can retain
// the full decimal in a tooltip without losing 64/78-bit precision.
export function formatCompactExact(value: string): string {
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(value)
  if (!match) return DASH

  const sign = match[1] ?? ''
  const whole = (match[2] ?? '0').replace(/^0+(?=\d)/, '') || '0'
  const fraction = match[3] ?? ''
  const digits = `${whole}${fraction}`
  if (!/[1-9]/.test(digits)) return '0.00'

  const index = compactExactUnitIndex(whole.length)
  if (index >= 0) return `${sign}${compactExactUnitText(digits, fraction.length, index)}`

  const firstNonZero = fraction.search(/[1-9]/)
  if (whole !== '0' || (firstNonZero >= 0 && firstNonZero <= 1)) {
    return `${sign}${roundExactFixed(digits, fraction.length, 2)}`
  }

  return firstNonZero < 0 ? '0.00' : formatSmallExact(sign, fraction, firstNonZero)
}

// Trading surfaces use a denser rule than market-data tables: keep at most
// three significant digits below 1K, and truncate compact K/M/B/T values to
// two decimals. Truncation is intentional so an estimate never visually
// overstates the execution-backed value (828449.31 -> 828.44K).
export function formatTradeAmountExact(value: string, signedPositive = false): string {
  const match = /^([+-]?)(\d+)(?:\.(\d+))?$/.exec(value.trim())
  if (!match) return DASH

  const whole = (match[2] ?? '0').replace(/^0+(?=\d)/, '') || '0'
  const fraction = match[3] ?? ''
  if (!/[1-9]/.test(`${whole}${fraction}`)) return '0'

  const sign = match[1] === '-' ? '-' : signedPositive ? '+' : ''
  const compact = tradeCompactUnit(whole.length)
  if (compact) return formatTradeCompact(sign, whole, fraction, compact)

  return formatTradeUnderThousand(sign, whole, fraction)
}

function formatTradeCompact(
  sign: string,
  whole: string,
  fraction: string,
  [suffix, scale]: readonly [string, number]
): string {
  const integerDigits = whole.length - scale
  const compactWhole = whole.slice(0, integerDigits)
  const compactFraction = `${whole.slice(integerDigits)}${fraction}`
    .padEnd(2, '0')
    .slice(0, 2)
    .replace(/0+$/, '')
  return `${sign}${compactWhole}${compactFraction ? `.${compactFraction}` : ''}${suffix}`
}

function formatTradeUnderThousand(sign: string, whole: string, fraction: string): string {
  if (whole !== '0') {
    const decimals = Math.max(0, 3 - whole.length)
    const kept = fraction.slice(0, decimals).replace(/0+$/, '')
    return `${sign}${whole}${kept ? `.${kept}` : ''}`
  }

  const firstNonZero = fraction.search(/[1-9]/)
  if (firstNonZero < 0) return '0'
  const significant = trimTrailingZeros(fraction.slice(firstNonZero, firstNonZero + 3))
  if (firstNonZero >= 3) return `${sign}0.0${toSubscript(firstNonZero)}${significant}`
  return `${sign}0.${'0'.repeat(firstNonZero)}${significant}`
}

/**
 * Position amounts (PnL, Holding, Bought, Sold) — denser than the shared trade
 * rule, because these sit four-to-a-row under the CTA and are read at a glance.
 *
 * Precision follows magnitude (operator rule, 2026-08-02):
 *   0.0₄999 -> 0.0₄9    subscript range: one significant digit
 *   0.0999  -> 0.099    two SIGNIFICANT digits, not two decimals
 *   0.954   -> 0.95     same rule, different magnitude
 *   100     -> 100      the shared rule already prints this
 *
 * Deliberately NOT folded into formatTradeAmountExact: that one also prints gas
 * and fees, where 0.00238 must keep its precision and this rule would trim it.
 */
/**
 * Below one nano of the quote unit there is no amount money can be: the chain
 * cannot represent it and no trade can produce it. Anything smaller is
 * arithmetic residue, and printing it as a figure turns noise into a finding —
 * canon once emitted an unrealized PnL of 1e-18 (two Rat products whose
 * rounded strings were equal), which read on screen as "+0.0₁₇1".
 *
 * The three statements this file makes must stay distinct:
 *   "—"  we do not know
 *   "0"  we know, and it is below the smallest amount that exists
 *   n    we know, and it is a number
 */
const POSITION_DUST_FLOOR = 1e-9

export function formatPositionAmountExact(value: string, signedPositive = false): string {
  const match = /^([+-]?)(\d+)(?:\.(\d+))?$/.exec(value.trim())
  if (!match) return DASH
  const magnitude = Math.abs(Number(value.trim()))
  if (Number.isFinite(magnitude) && magnitude > 0 && magnitude < POSITION_DUST_FLOOR) {
    // The SIGN survives the rounding. A sub-nano loss printed as "+0" is a loss
    // wearing a plus, and the cell around it is already red from the same
    // number — two contradicting statements about one position.
    if (match[1] === '-') return signedPositive ? '-0' : '0'
    return signedPositive ? '+0' : '0'
  }
  const whole = (match[2] ?? '0').replace(/^0+(?=\d)/, '') || '0'
  const fraction = match[3] ?? ''
  if (!/[1-9]/.test(`${whole}${fraction}`)) return '0'
  if (whole !== '0') return formatTradeAmountExact(value, signedPositive)

  const sign = match[1] === '-' ? '-' : signedPositive ? '+' : ''
  const firstNonZero = fraction.search(/[1-9]/)
  // TWO significant digits below 1, capped at THREE decimals (operator rules,
  // 2026-08-02 + 2026-08-05):
  //   0.954  -> 0.95      0.0999 -> 0.099      0.0072 -> 0.007
  // Counting SIGNIFICANT digits, not decimal places, is what keeps 0.0999 at
  // 0.099 while 0.954 becomes 0.95 — a fixed decimal count cannot do both.
  // In the 0.00x range the magnitude already says "small", so the three-decimal
  // cap leaves one significant digit (0.0072 -> 0.007, not 0.0072).
  // Once the value is small enough to need subscript notation the magnitude is
  // already carried by the zero run, so one digit is enough: 0.0₄999 -> 0.0₄9.
  if (firstNonZero >= 3) {
    return `${sign}0.0${toSubscript(firstNonZero)}${fraction.slice(firstNonZero, firstNonZero + 1)}`
  }
  const digits = Math.min(2, 3 - firstNonZero)
  const significant = trimTrailingZeros(fraction.slice(firstNonZero, firstNonZero + digits))
  if (!significant) return '0'
  return `${sign}0.${'0'.repeat(firstNonZero)}${significant}`
}

export function formatTradeRawAmount(
  raw: string | null | undefined,
  decimals: number,
  signedPositive = false
): string {
  if (
    typeof raw !== 'string' ||
    !/^(0|[1-9]\d*)$/.test(raw) ||
    !Number.isInteger(decimals) ||
    decimals < 0 ||
    decimals > 255
  )
    return DASH
  return formatTradeAmountExact(formatRawExactAmount(raw, decimals), signedPositive)
}

export function formatRawExactAmount(raw: string, decimals: number): string {
  if (!/^(0|[1-9]\d*)$/.test(raw) || !Number.isInteger(decimals) || decimals < 0 || decimals > 255)
    return DASH
  if (decimals === 0) return raw
  const padded = raw.padStart(decimals + 1, '0')
  const whole = padded.slice(0, -decimals)
  const fraction = padded.slice(-decimals).replace(/0+$/, '')
  return fraction ? `${whole}.${fraction}` : whole
}

function tradeCompactUnit(magnitude: number): readonly [string, number] | null {
  if (magnitude >= 13) return ['T', 12]
  if (magnitude >= 10) return ['B', 9]
  if (magnitude >= 7) return ['M', 6]
  if (magnitude >= 4) return ['K', 3]
  return null
}

// Descending, so a promotion is one step toward index 0. Mirrors COMPACT_UNITS.
const COMPACT_EXACT_UNITS = [
  { minDigits: 22, suffix: 'Sx', exponent: 21 },
  { minDigits: 19, suffix: 'Qi', exponent: 18 },
  { minDigits: 16, suffix: 'Q', exponent: 15 },
  { minDigits: 13, suffix: 'T', exponent: 12 },
  { minDigits: 10, suffix: 'B', exponent: 9 },
  { minDigits: 7, suffix: 'M', exponent: 6 },
  { minDigits: 4, suffix: 'K', exponent: 3 },
] as const

function compactExactUnitIndex(magnitude: number): number {
  for (let i = 0; i < COMPACT_EXACT_UNITS.length; i += 1) {
    if (magnitude >= (COMPACT_EXACT_UNITS[i]?.minDigits ?? Number.POSITIVE_INFINITY)) return i
  }
  return -1
}

// Same roll-up rule as formatCompact: rounding must never print 1000.00 of a
// unit that has a larger one above it. Measured on the digit string, not through
// Number — this path exists precisely to keep 64/78-bit values exact.
function compactExactUnitText(digits: string, fractionLength: number, index: number): string {
  let at = index
  let unit = COMPACT_EXACT_UNITS[at] ?? COMPACT_EXACT_UNITS[0]
  let text = roundExactFixed(digits, fractionLength + unit.exponent, 2)
  if (integerDigits(text) > 3 && at > 0) {
    at -= 1
    unit = COMPACT_EXACT_UNITS[at] ?? unit
    text = roundExactFixed(digits, fractionLength + unit.exponent, 2)
  }
  return `${text}${unit.suffix}`
}

function integerDigits(fixed: string): number {
  const dot = fixed.indexOf('.')
  return dot < 0 ? fixed.length : dot
}

function formatSmallExact(sign: string, fraction: string, firstNonZero: number): string {
  const significant = trimTrailingZeros(fraction.slice(firstNonZero, firstNonZero + 2))
  if (firstNonZero >= SUBSCRIPT_ZERO_RUN_MIN) {
    return `${sign}0.0${toSubscript(firstNonZero)}${significant}`
  }
  return `${sign}0.${'0'.repeat(firstNonZero)}${significant}`
}

function roundExactFixed(digits: string, scale: number, places: number): string {
  const numerator = BigInt(digits || '0')
  const denominator = 10n ** BigInt(Math.max(0, scale))
  const factor = 10n ** BigInt(places)
  const rounded = (numerator * factor + denominator / 2n) / denominator
  const text = rounded.toString().padStart(places + 1, '0')
  return `${text.slice(0, -places)}.${text.slice(-places)}`
}

// SMALL-MONEY RULE — one place, so Discover, search, Trenches, the pair page and
// the trade table can never disagree on how a dust figure is written.
//
// Below 0.01 a money value keeps two significant digits, and a run of >= 4
// leading zeros collapses into the same subscript notation prices already use
// (0.0₄16 = 0.000016). Digits are TRUNCATED, not rounded, so a displayed figure
// never overstates the underlying value: 0.0001683 → "0.00016", not "0.00017".
//
// Digits come from toExponential, not from arithmetic on the float: dividing by
// a power of ten reintroduces exactly the artifacts (4.199999…) this is meant to
// hide.
const SUBSCRIPT_ZERO_RUN_MIN = 4

function formatSmallMoney(v: number): string {
  const abs = Math.abs(v)
  const sign = v < 0 ? '-' : ''
  const [mantissa, exponent] = abs.toExponential(10).split('e')
  const exp = Number(exponent ?? '0')
  // zeros between the decimal point and the first significant digit.
  const zeros = -exp - 1
  const sig = trimTrailingZeros((mantissa ?? '').replace('.', '').slice(0, 2))
  if (zeros >= SUBSCRIPT_ZERO_RUN_MIN) return `${sign}0.0${toSubscript(zeros)}${sig}`
  return `${sign}0.${'0'.repeat(zeros)}${sig}`
}

function trimTrailingZeros(digits: string): string {
  return digits.replace(/0+$/, '') || '0'
}

// Whole-count formatter for txn counts and similar integers: thousands-separated up
// to 100k (8 -> "8", 1234 -> "1,234"), then compact (120000 -> "120.00K"). Use this
// for COUNTS, not formatCompact (which renders "8.00" for money). en-US locale pinned
// so tests compare strings without ICU drift. Non-finite / missing -> "—".
export function formatCount(v: number | null | undefined): string {
  if (typeof v !== 'number' || !Number.isFinite(v)) return DASH
  return v >= 100000 ? formatCompact(v) : Math.round(v).toLocaleString('en-US')
}

// Compact whole-count formatter for TIGHT cells — the pair page's Activity
// Buys/Sells, where the count shares one line with its USD volume ("4.15K/$82.4K").
// formatCount's thousands separators are right for a table column with room;
// here a busy token printed the full "4149" and crowded the money beside it.
// Below 1000 a count stays exact ("8", never formatCompact's "8.00"); from 1000
// it uses the same compact notation as every other figure on the page.
export function formatCountCompact(v: number | null | undefined): string {
  if (typeof v !== 'number' || !Number.isFinite(v)) return DASH
  const whole = Math.round(v)
  return Math.abs(whole) < 1000 ? String(whole) : formatCompact(whole)
}

// Subscript zero-run digits: 0.0₅1234 means 0.000001234 (5 zeros after
// "0.0"). PRICE-11 banned scientific notation for user-facing prices;
// this is the compact follow-up for chart axes / crosshair where
// "0.0000019247" doesn't fit. Falls back to plain decimals when the
// zero run is short enough to read directly.
const SUBSCRIPT_DIGITS = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉']

function toSubscript(n: number): string {
  return String(n)
    .split('')
    .map((d) => SUBSCRIPT_DIGITS[Number(d)] ?? d)
    .join('')
}

export function formatPriceSubscript(v: number): string {
  if (!Number.isFinite(v) || v === 0) return DASH
  const abs = Math.abs(v)
  const sign = v < 0 ? '-' : ''
  // Plain decimal range — reuse the PRICE-11 formatter.
  if (abs >= 0.001) return formatPrice(v)
  // toExponential gives correctly-rounded mantissa digits without the
  // float artifacts that toFixed(20) leaks (0.0001234 → ...12339999…).
  const parts = abs.toExponential(3).split('e')
  const mantissa = parts[0] ?? ''
  const zeros = -Number(parts[1] ?? '0') - 1
  if (zeros < 3) return formatPrice(v)
  const sig = mantissa.replace('.', '').replace(/0+$/, '')
  return `${sign}0.0${toSubscript(zeros)}${sig}`
}

// Axiom-style headline price: ~4 significant figures, with subscript zero-run
// notation for tiny values (0.0₄21 = 0.0000021). Keeps prices short — no
// 8-digit tails — for the token header and a narrow chart axis.
export function formatPriceAxiom(v: number): string {
  if (!Number.isFinite(v) || v === 0) return DASH
  const abs = Math.abs(v)
  if (abs >= 1000) return formatCompact(v)
  if (abs >= 1) return trimFixed(v, 4)
  if (abs >= 0.001) return String(Number(v.toPrecision(4)))
  return formatPriceSubscript(v)
}

// Share of supply (holders / top 10 / snipers). No leading plus — it is not a
// delta. API sends a 0–1 fraction. Zero is "0.00%"; missing is "—".
export function formatSharePct(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return DASH
  const pct = v >= 0 && v <= 1 ? v * 100 : v
  if (pct === 0) return '0.00%'
  return `${trimPercent(pct)}%`
}

// Percent with sign and 2-decimal precision.
// A flat change prints as "0.00%", never "+0.00%": the leading + claims an
// upward move, and a pool with no trades in the window did not move at all.
export function formatPct(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return DASH

  // Two decimals must never erase a real move. Keep ordinary percentages
  // compact, add precision only below one basis point, and use an explicit
  // bound once four decimals would still collapse to zero.
  if (v !== 0 && Math.abs(v) < 0.01) {
    if (Math.abs(v) < 0.0001) return `${v > 0 ? '+' : '-'}<0.0001%`
    return `${v > 0 ? '+' : ''}${trimFixed(v, 4)}%`
  }
  // A flat change keeps its documented two decimals; the sign is what that
  // canon protects, not the padding.
  if (v === 0) return '0.00%'
  const sign = v > 0 ? '+' : ''
  return `${sign}${trimPercent(v)}%`
}

// The single percent-precision rule for the whole product: precision follows
// magnitude and trailing zeros are never padded.
//   0.10 -> "0.1"   0.111 -> "0.11"   100.23 -> "100.2"   1000.14 -> "1000.1"
// Once the integer part carries the signal, a second decimal is noise. Anything
// rendering a percentage goes through here so one screen cannot show two
// conventions side by side.
export function trimPercent(v: number): string {
  return trimFixed(v, Math.abs(v) >= 100 ? 1 : 2)
}

// Pool/bonding fee rate from basis points (POOL-FEE-RATE): 30 → "0.3%",
// 100 → "1%", 25 → "0.25%". Non-positive/absent = unknown → "—" (a pool's
// fee is never actually zero; 0 means the getter hasn't answered).
export function formatFeeBps(feeBps: number | null | undefined): string {
  if (typeof feeBps !== 'number' || !Number.isFinite(feeBps) || feeBps <= 0) return DASH
  return `${trimFixed(feeBps / 100, 2)}%`
}

// TOKEN-NET-FEES display: cumulative network fees in GRAM are an
// order-of-magnitude signal, not an accounting readout — keep them short.
// >= 1000 → compact (1.25K); >= 1 → two truncated decimals (12.34); below 1 →
// three truncated decimals with trailing zeros trimmed (0.27, 0.021,
// 0.00473183 → "0.004"); below 0.001 TRUNCATE to the first significant digit
// and from 4 leading zeros collapse the run into a subscript like prices do
// (0.0004, 0.0000473 → "0.0₄4").
//
// Absent / non-positive → "0", not "—". The counter is event-driven from the
// token's deploy day and is never seeded from history (product decision
// 2026-07-07: trace-fee semantics would need an archive node), so a NULL is the
// counter's starting value rather than a figure we are still fetching. This is
// what /v1/pulse has always rendered; the token page now agrees.
export function formatFeeGram(v: number | null | undefined): string {
  if (typeof v !== 'number' || !Number.isFinite(v) || v <= 0) return '0'
  if (v >= 1000) return formatCompact(v)
  if (v >= 1) return trimFixed(Math.floor(v * 100) / 100, 2)
  if (v >= 0.001) return trimFixed(Math.floor(v * 1000) / 1000, 3)
  const parts = v.toExponential().split('e')
  const zeros = -Number(parts[1] ?? '0') - 1
  const firstDigit = (parts[0] ?? '1')[0]
  if (zeros >= 4) return `0.0${toSubscript(zeros)}${firstDigit}`
  return `0.${'0'.repeat(zeros)}${firstDigit}`
}

const rememberedJettonHolding = new Map<string, string>()

export function resetJettonHoldingMemoryForTest(): void {
  rememberedJettonHolding.clear()
}

// Scoped by wallet: a holding remembered for one wallet must never paint under
// another. Without an address there is nothing to own the figure, so it is
// remembered for this tab only.
function holdingKey(master: string, walletAddress: string | null | undefined): string {
  const addr = walletAddress?.trim()
  return addr ? `${addr}|${master}` : master
}

/** Jetton wallet figure: 0 once holdings have loaded (even if the token is
 *  absent). While the list has not arrived, reuse the last known amount so the
 *  header does not flash an em-dash over a balance we already showed. */
export function formatJettonHolding(
  holdings:
    | Array<{ token_master: string; balance: string; token_decimals?: number }>
    | null
    | undefined,
  master: string,
  fallbackDecimals: number,
  walletAddress?: string | null
): string {
  const want = canonicalTonAddress(master) ?? master
  const memo = holdingKey(want, walletAddress)
  const stored = figureKey(`jetton:${want}`, walletAddress)
  if (!holdings) {
    // Holdings have not arrived. We have not measured this wallet, so we do
    // not have a zero to report — paint the last real figure for THIS wallet
    // (across reloads), or report unknown and let the surface skeleton it.
    const remembered = rememberedJettonHolding.get(memo) ?? readLastKnownFigure(stored)
    return remembered ?? ''
  }
  const row = holdings.find(
    (item) => (canonicalTonAddress(item.token_master) ?? item.token_master) === want
  )
  const raw = row?.balance?.trim()
  if (!raw || raw === '0') {
    rememberedJettonHolding.set(memo, '0')
    writeLastKnownFigure(stored, '0')
    return '0'
  }
  const text = /^(0|[1-9]\d*)$/.test(raw)
    ? formatTradeRawAmount(raw, row?.token_decimals ?? fallbackDecimals)
    : formatTradeAmountExact(raw)
  const shown = text === DASH ? (rememberedJettonHolding.get(memo) ?? '0') : text
  rememberedJettonHolding.set(memo, shown)
  writeLastKnownFigure(stored, shown)
  return shown
}

// Short address: EQA8x...Pqz4. Returns short inputs unchanged so synthetic
// labels like "native" render verbatim.
/** An address cut to its ends: four characters, the ellipsis, four (the
 *  operator, 2026-10-21: "always 4 at the start and 4 at the end"). */
export function shortAddr(addr: string): string {
  if (!addr || addr.length < 10) return addr || DASH
  return `${addr.slice(0, 4)}…${addr.slice(-4)}`
}

// --- Token display fallbacks (NEW-TOKEN-METADATA-FALLBACK) --------------------
// Blank/unindexed deploys — and the ~30s window before metadata resolves —
// arrive with an empty name and a CA-prefix ticker. These helpers give every
// token a graceful, non-empty label + avatar without inventing data.

// True when `ticker` is a real symbol worth showing, not the backend's
// address-prefix fallback or a raw address. Type guard so callers narrow.
export function shouldDisplayTicker(ticker?: string, address?: string): ticker is string {
  if (!ticker) return false
  // Backend CA stubs are the first 8 of the bounceable address ("EQb4zZus").
  // startsWith is case-sensitive; EQB4… vs EQb4… used to leak the stub as a ticker.
  if (address && ticker.length >= 6 && address.toUpperCase().startsWith(ticker.toUpperCase())) {
    return false
  }
  return !/^(EQ|UQ)[A-Za-z0-9_-]{12,}(?:…[A-Za-z0-9_-]{4})?$/.test(ticker)
}

// Best human label for a token: real name → real ticker → short address.
// Never returns empty, so a nameless token still renders something meaningful.
export function tokenDisplayName(name?: string, ticker?: string, address?: string): string {
  const trimmedName = name?.trim()
  if (trimmedName) return trimmedName
  const trimmedTicker = ticker?.trim()
  if (shouldDisplayTicker(trimmedTicker, address)) return trimmedTicker
  return shortAddr(address ?? '')
}

// Whether a token has resolved identity worth trusting its image for. Nameless
// tokens whose only "ticker" is the CA prefix are unresolved blank deploys; their
// image_url is a shared placeholder, so callers render the monogram fallback.
export function hasRealTokenIdentity(name?: string, ticker?: string, address?: string): boolean {
  return Boolean(name?.trim()) || shouldDisplayTicker(ticker?.trim(), address)
}

function trimFixed(v: number, digits: number): string {
  return v
    .toFixed(digits)
    .replace(/(\.\d*?[1-9])0+$/, '$1')
    .replace(/\.0+$/, '')
}
